import { DATE_PERIOD_OPERATORS } from '@/data/filter-date-periods';
const ADVANCED_KEY = 'advanced';
/** Every key shape this module writes, for clearing a previous set out of the URL. */
const OWNED_KEY = /^(filters|groups)\[|^advanced$/;
const LIST_SEPARATOR = ',';
/**
 * Between a clause's parts: `filters[0]=type:eq:1`. Only the first two occurrences are
 * structural, so a value may contain the separator — `issued_by:contains:a:b` searches `a:b`.
 */
const PART_SEPARATOR = ':';
/** Operators naming their own range, so the clause needs no value at all. */
const PERIOD_OPERATORS = new Set(DATE_PERIOD_OPERATORS);
/**
 * How far to look for clauses. Reading stops at the first index the URL has nothing for, so
 * this only bounds a hand-edited query — the modal caps a real set far below it.
 */
const MAX_INDEX = 20;
const filterKey = (index) => `filters[${index}]`;
const groupKey = (group, index) => `groups[${group}][${index}]`;
// --- writing ---
/** A list joins into one param; nothing at all is written for an empty value. */
function writeValue(value) {
    const written = (Array.isArray(value) ? value : [value])
        .filter((entry) => entry !== null && entry !== undefined && entry !== '')
        .join(LIST_SEPARATOR);
    return written === '' ? null : written;
}
function writeClause(params, key, clause) {
    // a period preset carries no value — the backend derives its range from the operator.
    // `label` is left out too: a display-only cache the chips can rebuild, and it would
    // multiply the URL's length.
    const value = PERIOD_OPERATORS.has(clause.operator) ? null : writeValue(clause.value);
    const parts = [clause.field, clause.operator];
    if (value !== null) {
        parts.push(value);
    }
    params[key] = parts.join(PART_SEPARATOR);
}
/** Flatten a clause set into query params. */
export function filtersToQueryParams(set = {}) {
    const { filters = [], groups = [], advanced = false } = set;
    const params = {};
    filters.forEach((clause, index) => writeClause(params, filterKey(index), clause));
    groups.forEach((group, groupIndex) => {
        var _a;
        ((_a = group.clauses) !== null && _a !== void 0 ? _a : []).forEach((clause, index) => {
            writeClause(params, groupKey(groupIndex, index), clause);
        });
    });
    if (advanced) {
        params[ADVANCED_KEY] = 1;
    }
    return params;
}
// --- reading ---
/** A joined list becomes an array again; anything else stays the string it arrived as. */
function readValue(value, operator) {
    if (typeof value !== 'string') {
        return null;
    }
    if (!new Set(['in', 'between']).has(operator)) {
        return value;
    }
    return value
        .split(LIST_SEPARATOR)
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
}
/**
 * The clause at one key prefix, or null when the query holds none there — which is also how
 * a misspelt or malformed key is rejected: it is simply never looked up.
 */
function readClause(query, key) {
    const raw = query[key];
    if (typeof raw !== 'string') {
        return null;
    }
    const firstBreak = raw.indexOf(PART_SEPARATOR);
    // nothing before the separator is not a field
    if (firstBreak < 1) {
        return null;
    }
    // split by hand: `split(PART_SEPARATOR, 3)` discards the remainder rather than keeping it
    // as the third part, unlike PHP's `explode` limit
    const secondBreak = raw.indexOf(PART_SEPARATOR, firstBreak + 1);
    const hasValue = secondBreak !== -1;
    const operator = hasValue ? raw.slice(firstBreak + 1, secondBreak) : raw.slice(firstBreak + 1);
    if (operator === '') {
        return null;
    }
    return {
        field: raw.slice(0, firstBreak),
        operator,
        value: readValue(hasValue ? raw.slice(secondBreak + 1) : undefined, operator),
    };
}
/** Clauses from index 0 up, stopping at the first one the query has nothing for. */
function readClauses(query, key) {
    const clauses = [];
    for (let index = 0; index < MAX_INDEX; index += 1) {
        const clause = readClause(query, key(index));
        if (!clause) {
            break;
        }
        clauses.push(clause);
    }
    return clauses;
}
/** Read a clause set back out of query params. */
export function filtersFromQueryParams(query = {}) {
    const source = query !== null && query !== void 0 ? query : {};
    const groups = [];
    for (let group = 0; group < MAX_INDEX; group += 1) {
        const clauses = readClauses(source, (index) => groupKey(group, index));
        if (clauses.length === 0) {
            break;
        }
        groups.push({ match: 'or', clauses });
    }
    return {
        filters: readClauses(source, filterKey),
        groups,
        advanced: Boolean(source[ADVANCED_KEY]) && source[ADVANCED_KEY] !== '0',
    };
}
/**
 * Every key in `query` this module owns. Writing a smaller set has to blank the leftovers,
 * or clauses dropped from the filter would stay in the URL.
 */
export function filterQueryParamKeys(query = {}) {
    return Object.keys(query !== null && query !== void 0 ? query : {}).filter((key) => OWNED_KEY.test(key));
}
